/**
 * Elytra gliding (vanilla LivingEntity.updateFallFlying / travelFallFlying),
 * DOM-free and isolated from Player: the player calls `Glider.update` once a
 * tick (before moving) and reads `gliding`. Velocities are blocks / tick, pitch
 * is radians with positive = looking up (Player's convention).
 *
 * Vanilla rules kept: start by pressing jump in the air while falling with a
 * usable elytra in the chest slot; lift / drag from the look pitch; stops on
 * the ground or in liquid; 1 durability per second; hitting a wall at speed
 * hurts (kinetic energy, 10 * (speed lost) - 3).
 */

export const ELYTRA_DURABILITY = 432;
/** Durability points are spent once per this many gliding ticks. */
export const GLIDE_WEAR_TICKS = 20;
const GRAVITY = 0.08;

export interface GlideVel { x: number; y: number; z: number }

export interface GlideInput {
  velocity: GlideVel;
  /** Yaw (radians, Player convention: forward = (-sin, -cos)) and pitch (+ up). */
  yaw: number;
  pitch: number;
}

/** One tick of vanilla fall-flying velocity update (the caller then moves the player). */
export function glideStep(v: GlideVel, yaw: number, pitchUp: number): void {
  const pitch = -pitchUp; // vanilla xRot: negative = looking up
  const lx = -Math.sin(yaw) * Math.cos(pitch), ly = Math.sin(pitch), lz = -Math.cos(yaw) * Math.cos(pitch);
  const lookH = Math.hypot(lx, lz);
  const moveH = Math.hypot(v.x, v.z);
  const lift = Math.cos(pitch) * Math.cos(pitch);
  v.y += GRAVITY * (-1 + lift * 0.75);
  if (v.y < 0 && lookH > 0) {
    const d = v.y * -0.1 * lift;
    v.x += (lx * d) / lookH; v.y += d; v.z += (lz * d) / lookH;
  }
  if (pitch < 0 && lookH > 0) {
    const d = moveH * -Math.sin(pitch) * 0.04;
    v.x += (-lx * d) / lookH; v.y += d * 3.2; v.z += (-lz * d) / lookH;
  }
  if (lookH > 0) {
    v.x += ((lx / lookH) * moveH - v.x) * 0.1;
    v.z += ((lz / lookH) * moveH - v.z) * 0.1;
  }
  void ly;
  v.x *= 0.99; v.y *= 0.98; v.z *= 0.99;
}

/** Vanilla wall-crash damage from the horizontal speed lost (blocks / tick); 0 when not worth a heart. */
export function kineticDamage(speedBefore: number, speedAfter: number): number {
  const d = (speedBefore - speedAfter) * 10 - 3;
  return d > 0 ? d : 0;
}

export class Glider {
  gliding = false;
  /** Ticks glided since start. */
  ticks = 0;
  /** Horizontal speed before the last move (for the crash check). */
  private lastSpeed = 0;
  /** Fired once per second of gliding: wear the elytra down (return false when it breaks). */
  onWear: (() => void) | null = null;

  /**
   * Called every tick before movement. `hasElytra`: a usable (not broken) elytra is worn.
   * `jumpTapped`: jump key pressed this tick. Returns true while gliding.
   */
  update(p: { onGround: boolean; inLiquid: boolean; flying: boolean; spectator: boolean; hasElytra: boolean; jumpTapped: boolean; vy: number }): boolean {
    if (this.gliding) {
      if (!p.hasElytra || p.onGround || p.inLiquid || p.flying || p.spectator) { this.stop(); return false; }
      return true;
    }
    if (p.jumpTapped && p.hasElytra && !p.onGround && !p.inLiquid && !p.flying && !p.spectator && p.vy < 0) {
      this.gliding = true; this.ticks = 0;
      return true;
    }
    return false;
  }

  stop(): void { this.gliding = false; this.ticks = 0; this.rocketTicks = 0; }

  /** Physics + wear for one gliding tick; call `afterMove` with the real post-move velocity. */
  step(input: GlideInput): void {
    this.lastSpeed = Math.hypot(input.velocity.x, input.velocity.z);
    glideStep(input.velocity, input.yaw, input.pitch);
    if (this.rocketTicks > 0) {
      this.rocketTicks--;
      const v = input.velocity, pc = Math.cos(input.pitch);
      const lx = -Math.sin(input.yaw) * pc, ly = Math.sin(input.pitch), lz = -Math.cos(input.yaw) * pc;
      v.x += lx * 0.1 + (lx * 1.5 - v.x) * 0.5;
      v.y += ly * 0.1 + (ly * 1.5 - v.y) * 0.5;
      v.z += lz * 0.1 + (lz * 1.5 - v.z) * 0.5;
      this.onRocketTick?.();
      if (this.rocketTicks === 0) { const s = this.rocketStars; this.rocketStars = 0; this.onRocketEnd?.(s); }
    }
    this.lastSpeed = Math.hypot(input.velocity.x, input.velocity.z);
    if (++this.ticks % GLIDE_WEAR_TICKS === 0) this.onWear?.();
  }

  /** Remaining ticks of firework-rocket boost. */
  rocketTicks = 0;
  /** Per-tick trail hook while boosted. */
  onRocketTick: (() => void) | null = null;
  /** Vanilla FireworkRocketEntity lifetime: 10 * (duration + 1) + rand(0..5) + rand(0..6). */
  static rocketLife(duration: number, rnd: () => number = Math.random): number {
    return 10 * (Math.max(1, duration) + 1) + Math.floor(rnd() * 6) + Math.floor(rnd() * 7);
  }
  /** Use a rocket while gliding; returns false (nothing consumed) when not gliding. */
  rocketStars = 0;
  /** Fired when a boost runs out (the rocket's own burst: damages the player when it carries stars). */
  onRocketEnd: ((stars: number) => void) | null = null;
  fireRocket(duration: number, rnd: () => number = Math.random, stars = 0): boolean {
    if (!this.gliding) return false;
    this.rocketStars = stars;
    this.rocketTicks = Math.max(this.rocketTicks, Glider.rocketLife(duration, rnd));
    return true;
  }

  /** After the move: damage to take from a wall crash (0 if none). `speedNow` is the horizontal speed after collision. */
  crash(speedNow: number): number {
    return this.gliding ? kineticDamage(this.lastSpeed, speedNow) : 0;
  }
}
